import React, { useContext, useState } from 'react'

import { useMutation } from '@apollo/client'
import { once } from 'lodash'

import Input from 'tools/Input'
import notifier, { readableError } from 'tools/Notify'
import { CheckIcon } from 'tools/Uniform'

import { UPDATE_ORG } from 'hub/Org/graphql'
import GlobalContext, { R_SIGNON } from 'reducer/global'

import Section from './Section'
import { logGetStartedEvent } from './log'
import GetStartedContext, { R_START } from './reducer'
import { setPromo } from './utils'

const logSelectOrg = once(() => logGetStartedEvent('SelectOrg'))
const logCreateOrg = once(() => logGetStartedEvent('CreateOrg'))

function GetOrg() {
  const [{ user }] = useContext(GlobalContext)
  const [state, sDispatch] = useContext(GetStartedContext)
  const count = user.orgMemberships.length
  const [open, setOpen] = useState(count === 0)

  let heading = 'Organization Account'
  if (count > 1) {
    heading = 'Your Organization Accounts'
  }

  const chooseOrg = (orgId, org) => {
    org = orgId === state.orgId ? {} : org
    logSelectOrg()
    sDispatch({ type: R_START.SET_ORG, value: { org } })
  }

  return (
    <Section heading={heading}>
      <div className="i mb4">
        Your server registrations are assigned to an Organization Account. Please{' '}
        {count === 0
          ? 'create a new organization.'
          : 'select which account you would like these associated with.'}
      </div>
      <div className="flex flex-column gv2">
        {user.orgMemberships.map((m) => (
          <OrgRow
            member={m}
            key={m.id}
            onClick={chooseOrg}
            active={state.orgId === m.org.id}
          />
        ))}
        <div className="mt2">
          {open ? (
            <AddOrg onClose={() => setOpen(false)} first={count === 0} />
          ) : (
            <button
              className="w-100 tl large clear"
              onClick={() => setOpen(true)}
            >
              <i className="fas fa-plus mr2" /> Add Organization
            </button>
          )}
        </div>
      </div>
    </Section>
  )
}

function OrgRow({ member, active, onClick }) {
  return (
    <button
      className={`pv2 ph3 flex-items gh2 ${
        active ? 'bg-hilite' : 'clear'
      } b-none`}
      onClick={() => onClick(member.org.id, member.org)}
    >
      <CheckIcon checked={active} /> <OrgName mbr={member} />
    </button>
  )
}

function OrgName({ mbr }) {
  return (
    <div className="flexi-items">
      {mbr.org.name} <span className="gray f2 ml2">({mbr.org.shortId})</span>{' '}
      <span className="ml2 br5 bg-neutral ph2 pv1 f1 b">{mbr.role}</span>
    </div>
  )
}

export function AddOrg({ onClose, first }) {
  const [name, setName] = useState('')
  const [changeOrg] = useMutation(UPDATE_ORG)
  const [{ campaign: promo }, sDispatch] = useContext(GetStartedContext)
  const [, dispatch] = useContext(GlobalContext)
  const [, notifyD] = useContext(notifier.Context)

  return (
    <div className="flex-items">
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Organization Name"
        className="w-100"
      />
      <button
        className="ml3 large"
        onClick={() => {
          changeOrg({
            variables: { org: { name } },
            onCompleted({ orgChange }) {
              if (orgChange?.success) {
                logCreateOrg()
                const org = orgChange.result
                setPromo(changeOrg, org.id, promo)
                dispatch({ type: R_SIGNON.RELOAD_USER })
                sDispatch({ type: R_START.SET_ORG, value: { org } })
                onClose()
              } else {
                notifier.error(notifyD, orgChange.reason)
              }
            },
            onError(err) {
              notifier.error(notifyD, readableError(err))
            }
          })
        }}
      >
        Create
      </button>
      {!first && (
        <button className="ml3 neutral large" onClick={onClose}>
          Cancel
        </button>
      )}
    </div>
  )
}

export function GetOrgAddress() {
  const [, dispatch] = useContext(GlobalContext)
  const [state, sDispatch] = useContext(GetStartedContext)
  const [changeOrg] = useMutation(UPDATE_ORG)

  const address = state.org.address
  const orgId = state.org.id
  const heading = state.org.name + ' Address'

  const onChange = (field) => (val) => {
    const org = {
      ...state.org,
      address: {
        ...address,
        [field]: val
      }
    }
    sDispatch({ type: R_START.MERGE, value: { org } })
  }

  const onSave = (meta, val, good, bad) => {
    const value = JSON.stringify({ value: address })
    changeOrg({
      variables: {
        data: { orgId, type: 'companyaddr', value }
      },
      onCompleted({ orgChange }) {
        if (orgChange?.success) {
          good()
          dispatch({ type: R_SIGNON.RELOAD_USER }) // not the most efficient but works for now
        } else {
          bad(orgChange.reason)
        }
      }
    })
  }

  return (
    <Section heading={heading}>
      <div className="flex flex-column ga1">
        <Input
          label="street"
          spellCheck={false}
          value={address.street}
          onSave={onSave}
          onChange={onChange('street')}
          placeholder="Street"
          className="flex-grow-1"
        />
        <div className="flex ga1">
          <div className="flex-grow-1">
            <Input
              label="city"
              value={address.city}
              onSave={onSave}
              onChange={onChange('city')}
              placeholder="City"
            />
          </div>
          <div className="flex-grow-1">
            <Input
              label="state"
              value={address.state}
              onSave={onSave}
              onChange={onChange('state')}
              placeholder="State"
              className="flex-grow-1"
            />
          </div>
          <div className="flex-grow-1">
            <Input
              label="postal code"
              value={address.postal}
              onSave={onSave}
              onChange={onChange('postal')}
              placeholder="Postal Code"
              className="flex-grow-1"
            />
          </div>
        </div>
      </div>
    </Section>
  )
}

export default GetOrg
